import { useCallback, useEffect, useState } from "react";
import api from "../../../services/api";
import { FINANCING_INSIGHTS_PAGE_SIZE } from "../FinancingInsights/constants";
import { PATRIMONY_ITEMS_PAGE_SIZE } from "../constants/patrimonyPagination";
import {
  fetchAllFinancingInsights,
  fetchAllPropertyReturns,
  fetchFinancingInsights,
  fetchMarketIndices,
  fetchPropertyReturns,
} from "../services/ownerPatrimonyService";
import {
  IFinancingInsight,
  IMarketIndices,
  IOwnerKenloEndpoints,
  IPaginationMeta,
  IPropertyReturn,
  OwnerPatrimonyRequestStatus,
  YieldBasis,
} from "../types/ownerDashboard.types";

const EMPTY_MARKET_INDICES: IMarketIndices = {
  rent_adjustment_index: "ipca",
  ipca_12m_accumulated_pct: 0,
  valuation_index: "fipe_zap",
  fipe_zap_12m_accumulated_pct: 0,
  reference_month: null,
  ipca_monthly_history: [],
  fipe_zap_monthly_history: [],
};

const DEFAULT_PAGINATION: IPaginationMeta = {
  current_page: 1,
  per_page: PATRIMONY_ITEMS_PAGE_SIZE,
  total: 0,
  last_page: 1,
};

interface UsePatrimonySectionOptions {
  accessToken: string | null;
  enabled: boolean;
  endpoints?: IOwnerKenloEndpoints;
}

interface UsePatrimonySectionResult {
  marketIndices: IMarketIndices;
  marketIndicesStatus: OwnerPatrimonyRequestStatus;
  propertyReturns: IPropertyReturn[];
  propertyPagination: IPaginationMeta;
  propertyReturnsStatus: OwnerPatrimonyRequestStatus;
  financingInsights: IFinancingInsight[];
  financingPagination: IPaginationMeta;
  financingInsightsStatus: OwnerPatrimonyRequestStatus;
  allPropertyReturns: IPropertyReturn[];
  allFinancingInsights: IFinancingInsight[];
  yieldBasis: YieldBasis;
  setYieldBasis: (basis: YieldBasis) => Promise<void>;
  loadPropertyReturnsPage: (page: number) => Promise<void>;
  loadFinancingInsightsPage: (page: number) => Promise<void>;
  refreshPropertyReturns: () => Promise<void>;
  refreshFinancingInsights: () => Promise<void>;
  retryPropertyReturns: () => Promise<void>;
  retryMarketIndices: () => Promise<void>;
}

export function usePatrimonySection({
  accessToken,
  enabled,
  endpoints,
}: UsePatrimonySectionOptions): UsePatrimonySectionResult {
  const [marketIndices, setMarketIndices] = useState<IMarketIndices>(EMPTY_MARKET_INDICES);
  const [marketIndicesStatus, setMarketIndicesStatus] =
    useState<OwnerPatrimonyRequestStatus>("idle");
  const [propertyReturns, setPropertyReturns] = useState<IPropertyReturn[]>([]);
  const [propertyPagination, setPropertyPagination] =
    useState<IPaginationMeta>(DEFAULT_PAGINATION);
  const [propertyReturnsStatus, setPropertyReturnsStatus] =
    useState<OwnerPatrimonyRequestStatus>("idle");
  const [financingInsights, setFinancingInsights] = useState<IFinancingInsight[]>([]);
  const [financingPagination, setFinancingPagination] =
    useState<IPaginationMeta>(DEFAULT_PAGINATION);
  const [financingInsightsStatus, setFinancingInsightsStatus] =
    useState<OwnerPatrimonyRequestStatus>("idle");
  const [allPropertyReturns, setAllPropertyReturns] = useState<IPropertyReturn[]>([]);
  const [allFinancingInsights, setAllFinancingInsights] = useState<IFinancingInsight[]>([]);
  const [propertyPage, setPropertyPage] = useState(1);
  const [financingPage, setFinancingPage] = useState(1);
  const [yieldBasis, setYieldBasisState] = useState<YieldBasis>("purchase_value");

  const setAuthorizationHeader = useCallback(() => {
    if (accessToken) {
      api.defaults.headers.authorization = `Bearer ${accessToken}`;
    }
  }, [accessToken]);

  const loadRegisterMetadata = useCallback(async () => {
    if (!enabled || !accessToken) {
      return;
    }

    setAuthorizationHeader();

    try {
      const [allReturns, allFinancing] = await Promise.allSettled([
        fetchAllPropertyReturns(endpoints),
        fetchAllFinancingInsights(endpoints),
      ]);

      if (allReturns.status === "fulfilled") {
        setAllPropertyReturns(allReturns.value);
      }

      if (allFinancing.status === "fulfilled") {
        setAllFinancingInsights(allFinancing.value);
      }
    } catch {
      // NOTE: Register metadata is best-effort and must not block phase 2 rendering.
    }
  }, [accessToken, enabled, endpoints, setAuthorizationHeader]);

  const loadMarketIndices = useCallback(async () => {
    if (!enabled || !accessToken) {
      return;
    }

    setMarketIndicesStatus("loading");
    setAuthorizationHeader();

    try {
      const response = await fetchMarketIndices(endpoints);
      setMarketIndices(response);
      setMarketIndicesStatus("success");
    } catch {
      setMarketIndices(EMPTY_MARKET_INDICES);
      setMarketIndicesStatus("error");
    }
  }, [accessToken, enabled, endpoints, setAuthorizationHeader]);

  const loadPropertyReturnsPage = useCallback(
    async (page: number, basis: YieldBasis = yieldBasis) => {
      if (!enabled || !accessToken) {
        return;
      }

      setPropertyReturnsStatus("loading");
      setAuthorizationHeader();

      try {
        const response = await fetchPropertyReturns(
          { page, per_page: PATRIMONY_ITEMS_PAGE_SIZE, yield_basis: basis },
          endpoints
        );
        setPropertyReturns(response.items);
        setPropertyPagination(response.pagination);
        setPropertyPage(page);
        setYieldBasisState(basis);
        setPropertyReturnsStatus("success");
      } catch {
        setPropertyReturns([]);
        setPropertyPagination(DEFAULT_PAGINATION);
        setPropertyReturnsStatus("error");
      }
    },
    [accessToken, enabled, endpoints, setAuthorizationHeader, yieldBasis]
  );

  const setYieldBasis = useCallback(
    async (basis: YieldBasis) => {
      await loadPropertyReturnsPage(1, basis);
    },
    [loadPropertyReturnsPage]
  );

  const loadFinancingInsightsPage = useCallback(
    async (page: number) => {
      if (!enabled || !accessToken) {
        return;
      }

      setFinancingInsightsStatus("loading");
      setAuthorizationHeader();

      try {
        const response = await fetchFinancingInsights(
          { page, per_page: FINANCING_INSIGHTS_PAGE_SIZE },
          endpoints
        );
        setFinancingInsights(response.items);
        setFinancingPagination(response.pagination);
        setFinancingPage(page);
        setFinancingInsightsStatus("success");
      } catch {
        setFinancingInsights([]);
        setFinancingPagination(DEFAULT_PAGINATION);
        setFinancingInsightsStatus("error");
      }
    },
    [accessToken, enabled, endpoints, setAuthorizationHeader]
  );

  const loadPhase2 = useCallback(async () => {
    if (!enabled || !accessToken) {
      return;
    }

    setMarketIndicesStatus("loading");
    setPropertyReturnsStatus("loading");
    setFinancingInsightsStatus("loading");
    setAuthorizationHeader();

    const [indicesResult, returnsResult, financingResult] = await Promise.allSettled([
      fetchMarketIndices(endpoints),
      fetchPropertyReturns(
        { page: 1, per_page: PATRIMONY_ITEMS_PAGE_SIZE, yield_basis: "purchase_value" },
        endpoints
      ),
      fetchFinancingInsights({ page: 1, per_page: FINANCING_INSIGHTS_PAGE_SIZE }, endpoints),
    ]);

    if (indicesResult.status === "fulfilled") {
      setMarketIndices(indicesResult.value);
      setMarketIndicesStatus("success");
    } else {
      setMarketIndices(EMPTY_MARKET_INDICES);
      setMarketIndicesStatus("error");
    }

    if (returnsResult.status === "fulfilled") {
      setPropertyReturns(returnsResult.value.items);
      setPropertyPagination(returnsResult.value.pagination);
      setPropertyPage(1);
      setPropertyReturnsStatus("success");
    } else {
      setPropertyReturns([]);
      setPropertyPagination(DEFAULT_PAGINATION);
      setPropertyReturnsStatus("error");
    }

    if (financingResult.status === "fulfilled") {
      setFinancingInsights(financingResult.value.items);
      setFinancingPagination(financingResult.value.pagination);
      setFinancingPage(1);
      setFinancingInsightsStatus("success");
    } else {
      setFinancingInsights([]);
      setFinancingPagination(DEFAULT_PAGINATION);
      setFinancingInsightsStatus("error");
    }

    loadRegisterMetadata();
  }, [accessToken, enabled, endpoints, loadRegisterMetadata, setAuthorizationHeader]);

  const refreshPropertyReturns = useCallback(async () => {
    await loadPropertyReturnsPage(propertyPage);
    await loadRegisterMetadata();
  }, [loadPropertyReturnsPage, loadRegisterMetadata, propertyPage]);

  const refreshFinancingInsights = useCallback(async () => {
    await loadFinancingInsightsPage(financingPage);
    await loadRegisterMetadata();
  }, [financingPage, loadFinancingInsightsPage, loadRegisterMetadata]);

  const retryPropertyReturns = useCallback(async () => {
    await loadPropertyReturnsPage(propertyPage);
  }, [loadPropertyReturnsPage, propertyPage]);

  const retryMarketIndices = useCallback(async () => {
    await loadMarketIndices();
  }, [loadMarketIndices]);

  useEffect(() => {
    loadPhase2();
  }, [loadPhase2]);

  return {
    marketIndices,
    marketIndicesStatus,
    propertyReturns,
    propertyPagination,
    propertyReturnsStatus,
    financingInsights,
    financingPagination,
    financingInsightsStatus,
    allPropertyReturns,
    allFinancingInsights,
    yieldBasis,
    setYieldBasis,
    loadPropertyReturnsPage,
    loadFinancingInsightsPage,
    refreshPropertyReturns,
    refreshFinancingInsights,
    retryPropertyReturns,
    retryMarketIndices,
  };
}
